import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { CheatsheetCtx } from "./types";
import { CheatsheetDrawer } from "../components/CheatsheetDrawer";

interface CheatsheetApi {
  open: (ctx: CheatsheetCtx) => void;
  close: () => void;
  /** Register a card as the "hovered" one so ? / / hotkeys open its drawer. */
  registerHover: (ctx: CheatsheetCtx) => () => void;
}

const Ctx = createContext<CheatsheetApi | null>(null);

export function useCheatsheet(): CheatsheetApi {
  const v = useContext(Ctx);
  if (!v) throw new Error("useCheatsheet must be used inside <CheatsheetProvider>");
  return v;
}

/**
 * Thin wrapper a leaf component can use to declare its cheatsheet context.
 * Returns an object you spread onto the container: { onMouseEnter, onMouseLeave }.
 * Hovering updates the "last-hovered" state so the ? hotkey knows what
 * drawer to open.
 */
export function useHoverContext(ctx: CheatsheetCtx | null) {
  const api = useContext(Ctx);
  return useMemo(() => {
    if (!api || !ctx) return {};
    return {
      onMouseEnter: () => api.registerHover(ctx),
      // We deliberately don't clear on leave — the "last hovered" sticks
      // so the user can move mouse away then press ?. Multiple cards
      // fighting for it is fine; the latest wins.
    };
  }, [api, ctx]);
}

export function CheatsheetProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState<CheatsheetCtx | null>(null);
  const lastHover = useRef<CheatsheetCtx | null>(null);

  const api = useMemo<CheatsheetApi>(
    () => ({
      open: (ctx) => setOpen(ctx),
      close: () => setOpen(null),
      registerHover: (ctx) => {
        lastHover.current = ctx;
        return () => {
          if (lastHover.current === ctx) lastHover.current = null;
        };
      },
    }),
    []
  );

  // ? / / shortcut → open last-hovered card's drawer.
  // Esc handled inside the drawer itself.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const tag = (document.activeElement as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
      if ((e.key === "?" || e.key === "/") && lastHover.current) {
        e.preventDefault();
        setOpen(lastHover.current);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  const close = useCallback(() => setOpen(null), []);

  return (
    <Ctx.Provider value={api}>
      {children}
      <CheatsheetDrawer ctx={open} onClose={close} />
    </Ctx.Provider>
  );
}
